// pt.ts — Portuguese (pt-BR) catalog. `satisfies Catalog` is the whole safety net: a missing
// key, a misspelled key, or a function with the wrong params is a `check-types` failure.
//
// Translated per docs/superpowers/specs/locale-translator-contract.md (binding tone, do-not-
// translate list, plural rule) and the do-not-translate list imported by reference from
// docs/superpowers/specs/locale-glossary-vi.md §2. Register: "você" throughout — the standard
// modern pt-BR SaaS register, consistent with a founder-direct, technical, concrete tone.
import type { Catalog } from './en';

export const pt = {
  common: {
    close: 'Fechar',
  },
  status: {
    pending: 'pendente',
    crawling: 'rastreando',
    indexing: 'indexando',
    ready: 'pronto',
    error: 'erro',
    paused: 'pausado',
    hint: 'dica',
    deleting: 'excluindo',
  },
  time: {
    never: 'nunca',
    justNow: 'agora mesmo',
    inAMoment: 'em instantes',
    secondsAgo: ({ n }: { n: number }) => `${n}s atrás`,
    inSeconds: ({ n }: { n: number }) => `em ${n}s`,
    minutesAgo: ({ n }: { n: number }) => `${n} min atrás`,
    inMinutes: ({ n }: { n: number }) => `em ${n} min`,
    hoursAgo: ({ n }: { n: number }) => `${n}h atrás`,
    inHours: ({ n }: { n: number }) => `em ${n}h`,
    daysAgo: ({ n }: { n: number }) => `${n}d atrás`,
    inDays: ({ n }: { n: number }) => `em ${n}d`,
    monthsAgo: ({ n }: { n: number }) => `${n}m atrás`,
    inMonths: ({ n }: { n: number }) => `em ${n}m`,
    yearsAgo: ({ n }: { n: number }) => `${n}a atrás`,
    inYears: ({ n }: { n: number }) => `em ${n}a`,
  },
  shell: {
    impersonating: ({ email }: { email: string }) =>
      `Vendo como ${email} — você está se passando por este usuário.`,
    stopImpersonating: 'Parar',

    platformAdmin: 'Admin da plataforma',
    adminTabWorkspaces: 'Workspaces',
    adminTabBots: 'Bots',
    adminTabJobs: 'Jobs',
    adminTabPosts: 'Posts',
    adminTabFeedback: 'Feedback',
    adminTabPlayground: 'Playground',

    botsLoadError: "Não foi possível carregar seus bots.",
    botsLoadErrorInline: "Não foi possível carregar seus bots",
    reloadToRetry: 'Recarregue a página para tentar de novo.',

    navOverview: 'Visão geral',
    navUnanswered: 'Sem resposta',
    navConversations: 'Conversas',
    navLeads: 'Leads',
    navSources: 'Fontes',
    navPlayground: 'Playground',
    navAppearance: 'Aparência',
    navSettings: 'Configurações',
    groupNeedsYou: 'PRECISA DE VOCÊ',
    groupKnowledge: 'CONHECIMENTO',
    groupSetup: 'CONFIGURAÇÃO',

    closeMenu: 'Fechar menu',
    navigationLabel: 'Navegação',
    openNavigation: 'Abrir navegação',
    openAccountMenu: 'Abrir menu da conta',

    switchBotAriaLabel: ({ name }: { name: string }) => `${name}, trocar de bot`,
    botSwitcherHeader: ({ n, cap }: { n: number; cap: number | null }) =>
      `SEUS BOTS${cap !== null ? ` · ${n} DE ${cap} USADOS` : ''}`,
    allBots: 'Todos os bots',
    newBot: 'Novo bot',
    newBotOfCapUsed: ({ n, cap }: { n: number; cap: number }) => `Novo bot — ${n} de ${cap} usados`,

    pagesIndexed: 'Páginas indexadas',

    feedbackTrigger: 'Feedback',
    feedbackTypeGroupLabel: 'Tipo de feedback',
    feedbackTypeBug: 'Bug',
    feedbackTypeFeature: 'Sugestão de recurso',
    feedbackTypeQuestion: 'Pergunta',
    feedbackPlaceholder: 'O que aconteceu, ou o que tornaria o Embent melhor para você?',
    feedbackSent: 'Enviado — lemos todos.',
    feedbackSendError: 'Não foi possível enviar o feedback.',
    feedbackSending: 'Enviando…',
    feedbackSend: 'Enviar',
    feedbackDialogTitle: 'Enviar feedback',
    feedbackDialogDescription: 'Bug, sugestão de recurso ou pergunta — isso vai direto para a equipe.',

    workspaceSettings: 'Configurações do workspace',
    members: 'Membros',
    billingUsage: 'Cobrança e uso',
    switchWorkspace: 'Trocar workspace',
    accountLabelPrefix: 'Conta — ',
    account: 'Conta',
    signOut: 'Sair',
    themePrefix: 'Tema: ',
    languagePrefix: 'Idioma: ',
    nextPrefix: 'próximo: ',
    themeSystem: 'Sistema',
    themeDark: 'Escuro',
    themeLight: 'Claro',
    roleOwner: 'Proprietário',
    roleAdmin: 'Admin',
    roleMember: 'Membro',
    workspaceSubline: ({ plan, role }: { plan: string | null; role: string }) =>
      plan ? `${plan} · você é ${role}` : `Você é ${role}`,
    workspaceMenuAriaLabel: ({ name }: { name: string }) => `${name}, menu de workspace e conta`,

    usageWarning: ({ pct }: { pct: number }) =>
      `Você já usou ${pct}% das suas mensagens mensais — faça upgrade para manter todas as conversas fluindo.`,
    dismiss: 'Dispensar',

    renewsOn: ({ date }: { date: string }) => `Renova em ${date}`,
    resetsOn: ({ date }: { date: string }) => `Reinicia em ${date}`,
    answersPaused: 'Novas respostas pausadas — acima de 110% do limite',
    messagesLeft: ({ n }: { n: string }) => `${n} mensagens restantes este mês`,
    badgeStillShown: ({ plan }: { plan: string }) => `${plan} ainda mostra o selo "Powered by Embent"`,
    badgeOptional: ({ plan }: { plan: string }) => `${plan} torna o selo "Powered by Embent" opcional`,
    upgradeCta: ({ price }: { price: number }) => `Fazer upgrade — $${price}/mês`,
    resumeAnswersCta: ({ price }: { price: number }) => `Retomar respostas — $${price}/mês`,
    resumeAnswersBenefit: 'Fazer upgrade retoma as respostas imediatamente',
    upgradeBenefit: ({
      plan,
      messages,
      bots,
      seats,
    }: {
      plan: string;
      messages: string;
      bots: number;
      seats: number;
    }) => `${plan} adiciona ${messages} mensagens, ${bots} bots e ${seats} assento${seats === 1 ? '' : 's'}`,
  },
  bots: {
    noSources: 'sem fontes',
    allSourcesReady: ({ n }: { n: number }) => `${n} · todas prontas`,
    sourceStatusRollup: ({ n, status }: { n: number; status: string }) => `${n} · ${status}`,
    paused: 'Pausado',
    sourcesRemoving: ({ n }: { n: number }) => `${n} fonte${n === 1 ? '' : 's'} · removendo`,
    sourcesSyncError: ({ n }: { n: number }) => `${n} fonte${n === 1 ? '' : 's'} · erro de sincronização`,
    sourcesSyncing: ({ n }: { n: number }) => `${n} fonte${n === 1 ? '' : 's'} · sincronizando`,
    messagesUnanswered: ({ messages, unanswered }: { messages: number; unanswered: number }) =>
      `${messages} ${messages === 1 ? 'mensagem' : 'mensagens'} · ${unanswered} sem resposta`,
    botsCount: ({ n }: { n: number }) => `${n} ${n === 1 ? 'bot' : 'bots'}`,
    botsOfCapUsed: ({ n, cap }: { n: number; cap: number }) => `${n} de ${cap} usados`,

    colBot: 'Bot',
    colStatus: 'Status',
    colMessages14d: 'Mensagens · 14d',
    colUnanswered: 'Sem resposta',
    colSources: 'Fontes',
    pageTitle: 'Bots',
    emptyTitle: 'Crie seu primeiro bot',
    emptyDescription:
      'Aponte para o seu site ou documentação — ele rastreia o conteúdo e começa a responder perguntas dos visitantes.',
    planIncludesBots: ({ cap }: { cap: number }) => `Seu plano inclui ${cap} bot${cap === 1 ? '' : 's'}`,
    upgradeGateDescription: ({ name, priceUsd, maxBots }: { name: string; priceUsd: number; maxBots: number }) =>
      `${name} ($${priceUsd}/mês) roda até ${maxBots} bots — um por site, cada um com seu próprio conhecimento e visual.`,
    comparePlansLink: 'Comparar planos →',
    newBotDialogTitle: 'Novo bot',
    botNameFieldLabel: 'Nome',
    botNamePlaceholder: 'Bot de Suporte da Acme',
    creatingCta: 'Criando…',
    createCta: 'Criar',
  },
  auth: {
    emailLabel: 'E-mail',
    passwordLabel: 'Senha',
    genericFormError: 'Algo deu errado. Tente novamente.',
    continueWithGoogle: 'Continuar com o Google',
    backToSignIn: 'Voltar para o login',

    signInTitle: 'Entrar',
    signInSubtitle: 'Bem-vindo de volta ao Embent.',
    passwordJustResetNotice: 'Senha atualizada — entre com sua nova senha.',
    forgotPasswordLink: 'Esqueceu a senha?',
    unverifiedResendNotice: 'Confira sua caixa de entrada — reenviamos o e-mail de verificação.',
    signingIn: 'Entrando…',
    signInCta: 'Entrar',
    noAccountPrompt: 'Não tem uma conta? ',
    signUpLink: 'Cadastre-se',

    signUpTitle: 'Crie sua conta',
    signUpSubtitle: 'Comece a responder perguntas de visitantes em minutos.',
    nameLabel: 'Nome',
    creatingAccount: 'Criando conta…',
    createAccountCta: 'Criar conta',
    haveAccountPrompt: 'Já tem uma conta? ',
    signInLink: 'Entrar',

    checkEmailTitle: 'Verifique seu e-mail',
    checkEmailSubtitle: 'Enviamos um link de verificação. Ele costuma chegar em até um minuto.',
    checkEmailBody: 'Clique no link do e-mail para ativar sua conta.',

    verifyFailedTitle: 'Falha na verificação',
    verifiedTitle: 'E-mail verificado',
    verifyFailedBody: 'Esse link de verificação é inválido ou expirou.',
    verifiedBody: 'Tudo pronto — levando você para o seu painel…',

    requestResetTitle: 'Redefina sua senha',
    requestResetSubtitlePending: 'Digite seu e-mail e enviaremos um link de redefinição.',
    requestResetSubtitleSent: 'Se esse e-mail tiver uma conta Embent, enviamos um link de redefinição.',
    requestResetBodySent: 'Confira sua caixa de entrada. O link expira em 1 hora.',
    sendingResetLink: 'Enviando…',
    sendResetLinkCta: 'Enviar link de redefinição',

    resetLinkInvalidTitle: 'Link de redefinição inválido',
    resetLinkInvalidBody: 'Esse link de redefinição de senha está sem o token, ou já foi usado.',
    requestNewLinkCta: 'Solicitar um novo link',
    chooseNewPasswordTitle: 'Escolha uma nova senha',
    chooseNewPasswordSubtitle: 'Escolha algo que você ainda não tenha usado.',
    newPasswordLabel: 'Nova senha',
    resetLinkExpiredFallback: 'Esse link de redefinição é inválido ou expirou.',
    savingPassword: 'Salvando…',
    saveNewPasswordCta: 'Salvar nova senha',

    inviteNotValidTitle: 'Convite inválido',
    goToEmbentLink: 'Ir para o Embent',
    inviteInvalidFallback: 'Este convite não é mais válido.',
    inviteTitle: ({ workspaceName }: { workspaceName: string }) => `Você foi convidado para ${workspaceName}`,
    inviteDescription: ({ email }: { email: string }) =>
      `Enviado para ${email} — entre ou crie uma conta com esse e-mail para aceitar.`,
    inviteSignInCta: 'Entrar',
    inviteSignUpCta: 'Criar uma conta',
    wrongAccountTitle: 'Conta errada',
    signOutCta: 'Sair',
    inviteAcceptFallback: 'Algo deu errado ao aceitar este convite.',
    joiningWorkspaceTitle: ({ workspaceName }: { workspaceName: string }) => `Entrando em ${workspaceName}…`,
    joiningWorkspaceBody: 'Só um instante.',

    eventLabels: {
      newLead: 'novo lead',
      unansweredDigest: 'resumo de sem resposta',
      crawlFailed: 'falha no rastreamento',
      weeklySummary: 'resumo semanal',
    },
    unsubscribeLinkExpiredTitle: 'Este link expirou',
    unsubscribeLinkExpiredBody: "Gerencie as notificações nas configurações do bot, dentro do painel.",
    openDashboardCta: 'Abrir painel',
    unsubscribedTitle: 'Você cancelou a inscrição',
    unsubscribedBody: ({ eventLabel, botName }: { eventLabel: string; botName: string | null }) =>
      `Você não vai mais receber e-mails de ${eventLabel} para ${botName ?? 'este bot'}. Você pode reativar nas configurações do bot.`,

    notFoundEyebrow: 'Página não encontrada',
    notFoundTitle: 'Não há nada neste endereço',
    notFoundBody: 'O link pode estar desatualizado, ou a página pode ter mudado de lugar.',
    notFoundCta: 'Ir para seus bots',

    landingBotsErrorFallback: 'Algo deu errado ao carregar seus bots.',
    landingGoToBotsCta: 'Ir para seus bots →',
  },
  onboarding: {
    skipForNow: 'Pular por agora',
    invalidWebsiteUrl: 'Digite uma URL de site válida, ex.: https://empresa.com.br',
    websiteUrlLabel: 'URL do site',
    genericFallbackError: 'Algo deu errado.',

    crawlDefaultHostname: 'seu site',
    noSourceToReplace: 'Nenhuma fonte para substituir ainda.',

    stepWebsite: 'Site',
    stepCrawl: 'Rastreamento',
    stepTryIt: 'Testar',
    stepLook: 'Visual',
    stepGoLive: 'Publicar',
    stepMobileReadout: ({
      current,
      total,
      label,
    }: {
      current: number;
      total: number;
      label: string;
    }) => `Passo ${current} de ${total} · ${label}`,
    stepProgressAriaLabel: ({
      current,
      total,
      label,
    }: {
      current: number;
      total: number;
      label: string;
    }) => `Progresso da configuração: passo ${current} de ${total}, ${label}`,
    stepTablistAriaLabel: 'Progresso da configuração',

    websiteHeading: 'Adicione seu site',
    websiteSubtitle:
      'O Embent lê o seu site e monta um bot que responde com citações — nada inventado. A maioria dos sites fica pronta em poucos minutos.',
    botNameLabel: 'Nome do bot',
    botNamePlaceholder: 'Suporte Acme',
    websiteUrlPlaceholder: 'acme.vn',
    websiteUrlHelper: 'Vamos encontrar suas páginas automaticamente. Você pode adicionar mais fontes depois.',
    botNameRequired: 'Digite um nome para o seu bot.',
    websiteStarting: 'Iniciando…',
    websiteSubmitCta: 'Começar a ler meu site',

    crawlHeading: ({ hostname }: { hostname: string }) => `Lendo ${hostname}…`,
    crawlSubtitleReady: 'Tudo pronto — todas as páginas estão indexadas e prontas para responder.',
    crawlSubtitleSyncing: 'Você não precisa esperar aqui — experimente seu bot enquanto ele termina.',
    crawlStatusLines: {
      pending: ['Na fila — começando em breve…', 'Se preparando para começar…'],
      crawling: ['Encontrando páginas no site…', 'Seguindo links…', 'Lendo páginas…'],
      indexing: ['Criando o índice de busca…', 'Processando o que foi lido…'],
    },
    crawlPagesIndexed: ({ n }: { n: number }) => `${n} páginas indexadas`,
    crawlNoSourceYet: 'Ainda não encontramos uma fonte para este bot.',
    crawlErrorLead:
      'A causa mais provável é um erro de digitação no endereço — confira abaixo e tente de novo.',
    crawlReplaceUrlCta: 'Tentar outro endereço',
    crawlReplaceUrlUpdating: 'Atualizando…',
    crawlReplaceUrlHelper: 'Vamos remover a tentativa que falhou e recomeçar com este endereço.',
    crawlRetryCta: 'Tentar de novo',
    crawlRetrying: 'Tentando de novo…',
    crawlRetryHelper:
      'Se o endereço estiver certo e o site só esteve fora do ar rapidamente, tente de novo. Sites ' +
      'que precisam de JavaScript para mostrar o conteúdo podem ser tratados com uma configuração ' +
      'disponível depois da configuração inicial, na tela de Fontes.',
    crawlCtaSyncing: 'Testar seu bot enquanto ele termina →',
    crawlCtaReady: 'Próximo: testar seu bot →',

    tryItHeading: 'Pergunte qualquer coisa',
    tryItSubtitleWithPages: ({ n }: { n: number }) =>
      `Ele já conhece ${n} página${n === 1 ? '' : 's'} — e ainda está aprendendo o resto. Toda resposta mostra suas fontes.`,
    tryItSubtitleNoPages:
      'Ele ainda está lendo o seu site — tente perguntar mesmo assim; as respostas só tendem a melhorar a partir daqui.',
    tryItSamplePrompt: `Tente: "Qual é a política de devolução de vocês?"`,
    tryItPlaceholder: 'Pergunte sobre entrega, preços, qualquer coisa…',
    tryItSending: 'Enviando…',
    tryItSendCta: 'Enviar',
    tryItNextCta: 'Ficou bom — deixar do meu jeito',

    lookHeading: 'Deixe do jeito do seu site',
    lookSubtitle:
      'Escolha a cor da sua marca — a prévia atualiza enquanto você digita. Você pode ajustar tudo com calma depois em Aparência.',
    brandColorLabel: 'Cor da marca',
    colorHexAriaLabel: 'Valor hexadecimal da cor',
    welcomeMessageLabel: 'Mensagem de boas-vindas',
    lookSaving: 'Salvando…',
    lookContinueCta: 'Continuar',

    goLiveHeading: 'Um código e você está no ar',
    goLivePasteBeforePrefix: 'Cole isto antes do fechamento',
    goLivePasteBeforeSuffix: 'no seu site. É só isso — o bot aparece no canto inferior direito.',
    goLiveInstallGuidesLink: 'Guias de instalação por plataforma',
    goLiveFirstAnswerNotice: 'Vamos te avisar por e-mail assim que o widget responder ao primeiro visitante.',
    goLiveDashboardCta: 'Ir para o painel',
    goLiveViewSourcesCta: 'Ver fontes',
  },
  overview: {
    title: 'Visão geral',
    liveOnPrefix: 'No ar em ',
    answeringSince: ({ date }: { date: string }) => `respondendo desde ${date}`,
    testInPlaygroundCta: 'Testar no Playground',
    addSourcesCta: 'Adicionar fontes',
    botDetailsLoadError: ({ message }: { message: string }) => `Não foi possível carregar os detalhes do bot — ${message}`,

    messagesLabel: 'Mensagens · últimos 14 dias',
    conversationsLink: 'Conversas →',
    vsPreviousPeriod: 'vs. 14 dias anteriores',
    leadsLabel: 'Leads capturados',
    newLeadsCount: ({ n }: { n: number }) => `${n} novos`,
    leadsThisWeek: ({ n }: { n: number }) => `${n} esta semana`,
    viewLeadsLink: 'Ver leads →',
    unansweredLabel: 'Perguntas sem resposta',
    fixThemLink: 'Resolver →',

    knowledgeHeading: 'Conhecimento',
    pointAtSite: 'Aponte para o seu site',
    addSourceLink: 'Adicionar uma fonte →',
    addSourcesLink: 'Adicionar fontes →',
    pagesIndexed: 'Páginas indexadas',
    sourcesCountLabel: 'Fontes',
    lastSyncedLabel: 'Última sincronização',
    manageSourcesLink: 'Gerenciar fontes →',
    resyncing: 'Ressincronizando…',
    allSyncing: 'Tudo sincronizando',
    resyncCta: 'Ressincronizar',
    resyncNothingToSync: 'Nada para sincronizar — todas as fontes já estão sincronizando.',
    resyncGenericError: "Não foi possível iniciar a ressincronização — tente novamente.",

    latestConversationsHeading: 'Últimas conversas',
    allConversationsLink: 'Todas as conversas →',
    noConversationsEmpty: 'Tranquilo nos últimos 14 dias — novas conversas vão aparecer aqui.',
    noMessageFallback: '(sem mensagem)',
    leadCapturedTag: 'Lead capturado',
    hasUnansweredTag: 'Tem pergunta sem resposta',

    knowledgeAllReady: 'Tudo pronto',
    knowledgeSyncing: 'Fontes sincronizando',
    knowledgeNeedsAttention: 'Uma fonte precisa de atenção',
    knowledgeRemoving: 'Removendo fontes',

    createdSubtitle: ({ time }: { time: string }) => `Criado ${time}`,
    zeroStatMessagesHint: 'Começa na primeira pergunta',
    zeroStatLeadsHint: 'Nome e e-mail, no meio da conversa',
    zeroStatUnansweredHint: 'Nada para resolver ainda',
    heroReadyHeadline: 'Seu bot está pronto para responder',
    heroIndexingHeadline: 'Ainda lendo o seu site',
    heroEmptyHeadline: 'Adicione uma fonte para começar',
    heroEmptyBody:
      "Ele ainda não tem nada para ler. Adicione uma fonte e ele estará pronto para responder em minutos.",
    heroIndexingErrorBody:
      "Uma fonte precisa de atenção — ela não vai terminar sozinha. Confira o card de Conhecimento para ver o que está errado, e coloque no seu site assim que estiver corrigido.",
    heroIndexingPrefix: "Ele ainda está lendo",
    heroIndexingNoSourceFallback: 'sua fonte',
    heroIndexingSuffix: ' — volte a checar em alguns minutos, depois coloque no seu site.',
    heroReadyBodyPrefix: ({ n }: { n: number }) => `Ele leu ${n} ${n === 1 ? 'página' : 'páginas'}`,
    heroReadyFromDomain: ' de ',
    heroReadyBodySuffix:
      ' e já sabe o suficiente para responder perguntas sobre elas. Falta um passo: colocar no seu site.',
    onlyPagesNudge: ({ n }: { n: number }) =>
      `Só ${n} ${n === 1 ? 'página' : 'páginas'}? Adicione suas páginas de FAQ, preços ou políticas para ele ter mais material.`,
    addSourceCta: 'Adicionar uma fonte',
    addSourceHelperText:
      'Leva cerca de um minuto. O código de incorporação vai estar esperando bem aqui assim que ele tiver algo para responder.',
    pasteBeforeBodyLabel: 'COLE ISTO ANTES DE </BODY>',
    emailSnippetLink: 'Enviar o código por e-mail para o meu desenvolvedor',
    emailSnippetSubject: ({ botName }: { botName: string }) => `Incorporar ${botName} no nosso site`,
    emailSnippetBody: ({ snippet }: { snippet: string }) => `Cole isto antes do fechamento da tag </body>:\n\n${snippet}`,
    whatHappensNextHeading: 'O que acontece a seguir',
    stepVisitorAsksTitle: 'Um visitante pergunta algo',
    stepVisitorAsksBody: 'O balão fica no canto inferior direito de toda página onde o código está. Nada mais para configurar.',
    stepEmailAnswerTitle: 'Enviamos a primeira resposta por e-mail',
    stepEmailAnswerBody: "Assim você lê exatamente o que ele disse, nas palavras do seu cliente, antes de qualquer outra pessoa.",
    stepUnansweredTitle: "O que ele não conseguiu responder cai aqui",
    stepUnansweredBody: 'Ele se recusa em vez de inventar. Cada recusa vira uma linha para você resolver.',
    stepAddSourceTitle: 'Adicione uma fonte',
    stepAddSourceBody:
      'Aponte para o seu site, ou envie arquivos e texto colado — ele precisa de algo para ler antes de conseguir responder.',
    stepAddSourceVisitorTitle: 'Um visitante pergunta, você recebe a resposta por e-mail',
    countersHeading: 'OS CONTADORES COMEÇAM COM SEU PRIMEIRO VISITANTE',

    needsYouHeading: 'Precisa de você',
    allUnansweredLink: 'Todas sem resposta →',
    needsYouEmpty: 'Nada para resolver — seu bot respondeu tudo o que foi perguntado.',
    needsYouIntro: "Seu bot disse que não sabia. Responda uma vez e ele sabe para sempre.",
    askedSummary: ({ n, time }: { n: number; time: string }) =>
      `${n === 1 ? 'Perguntado uma vez' : `Perguntado ${n} vezes`} · última vez ${time}`,
    writeAnswerCta: 'Escrever resposta',
    cancelCta: 'Cancelar',
    answerPlaceholder: "Escreva a resposta que você daria a este visitante. O bot vai usá-la a partir de agora.",
    addingCta: 'Adicionando…',
    addAsQaSourceCta: 'Adicionar como fonte de P&R',
    saveAnswerError: "Não foi possível salvar essa resposta — tente novamente.",
  },
  sources: {
    pageTitle: 'Fontes',
    sourcesSubtitle: ({
      n,
      pageCount,
      lastSynced,
    }: {
      n: number;
      pageCount: number;
      lastSynced: string;
    }) =>
      `${n} fonte${n === 1 ? '' : 's'} · ${pageCount} página${pageCount === 1 ? '' : 's'} · última sincronização ${lastSynced}`,
    botLoadError: ({ message }: { message: string }) => `Não foi possível carregar este bot — ${message}`,

    typeUrl: 'URL',
    typeSitemap: 'Sitemap',
    typeText: 'Texto',
    typeFile: 'Arquivo',

    colType: 'Tipo',
    colUri: 'URI',
    colStatus: 'Status',
    colRenderedMode: 'Modo renderizado',
    colPages: 'Páginas',
    colLastSynced: 'Última sincronização',
    colActions: 'Ações',

    urlFieldLabel: 'URL da página',
    urlPlaceholder: 'https://example.com/docs',
    sitemapFieldLabel: 'URL do sitemap',
    sitemapPlaceholder: 'https://example.com/sitemap.xml',
    textTitleFieldLabel: 'Título',
    textTitlePlaceholder: 'Política de reembolso',
    textContentFieldLabel: 'Conteúdo',
    textContentPlaceholder: 'Cole o texto que você quer que o bot conheça…',
    fileFieldLabel: 'Arquivo',
    fileDropPrompt: 'Solte um arquivo aqui, ou clique para procurar',
    fileDropCaption: ({ extensions, maxSize }: { extensions: string; maxSize: string }) =>
      `${extensions} · até ${maxSize}`,
    fileTooLarge: ({ maxSize }: { maxSize: string }) => `Esse arquivo é maior que o limite de ${maxSize}.`,
    fileTypeNotSupported: ({ ext, extensions }: { ext: string; extensions: string }) =>
      `${ext ? `.${ext}` : 'Esse arquivo'} não é aceito — use ${extensions}.`,
    filesDroppedNote: ({ name, count }: { name: string; count: number }) =>
      `Usando "${name}" — ${count} outro${count === 1 ? '' : 's'} arquivo${count === 1 ? '' : 's'} ignorado${count === 1 ? '' : 's'}.`,
    fileDropNotAFile: "Isso não era um arquivo — arraste um arquivo do seu computador.",
    fileTitleFieldLabel: 'Título (opcional)',
    fileTitlePlaceholder: 'Usa o nome do arquivo por padrão',

    addingCta: 'Adicionando…',
    addSourceCta: 'Adicionar fonte',

    emptyTitle: 'Aponte para o seu site',
    emptyDescription: "Adicione uma URL, sitemap, texto ou arquivo acima e vamos começar a ler.",

    reviewPagesLink: 'Revisar páginas',
    nearEmptyInfoAriaLabel: ({ count }: { count: number }) =>
      `${count} página${count === 1 ? '' : 's'} ${count === 1 ? 'veio' : 'vieram'} vazia${count === 1 ? '' : 's'}`,

    resyncCta: 'Ressincronizar',
    deleteCta: 'Excluir',
    cancelCta: 'Cancelar',
    syncInProgressError: 'Sincronização em andamento — tente novamente mais tarde',

    deleteConfirmTitle: 'Excluir esta fonte?',
    deleteConfirmDescriptionPrefix: 'Remove ',
    deleteConfirmDescriptionSuffix: ' e tudo que ela indexou. Isso não pode ser desfeito.',

    renderedModeExplainerAriaLabel: 'O que é modo renderizado?',
    renderedModeExplainerBody1Bold: 'Modo renderizado',
    renderedModeExplainerBody1Rest:
      ' roda cada página em um navegador de verdade antes de lê-la, então conteúdo que só aparece depois que o JavaScript roda também é capturado.',
    renderedModeExplainerBody2:
      "Deixe desativado para sites normais — é mais rápido e mais barato. Ative quando uma página parecer vazia para o crawler (aplicativos de página única, painéis, qualquer coisa que carrega o texto depois que a página abre).",
    renderedModeExplainerBody3:
      'Contrapartida: o rastreamento fica mais lento e usa mais do seu orçamento de páginas por execução.',

    renderedModeLabel: 'Modo renderizado',
    modeRendered: 'Renderizado',
    modeStandard: 'Padrão',
    switchModeTitle: ({ mode }: { mode: string }) => `Mudar para o modo ${mode}?`,
    switchModeDescription: ({ mode }: { mode: string }) =>
      `Isso refaz o rastreamento da fonte no modo ${mode}. Páginas que voltarem diferentes são reindexadas e as que não existem mais são removidas — o índice atual continua disponível até o novo rastreamento terminar.`,
    switchModeCta: ({ mode }: { mode: string }) => `Mudar para o modo ${mode}`,
  },
  sourceDetail: {
    crawledPages: ({ n }: { n: number }) => `Rastreou ${n} página${n === 1 ? '' : 's'}.`,
    crawledPagesWithSkipped: ({ n, failedCount }: { n: number; failedCount: number }) =>
      `Rastreou ${n} página${n === 1 ? '' : 's'}. ${failedCount} página${failedCount === 1 ? '' : 's'} não ${failedCount === 1 ? 'carregou' : 'carregaram'} e ${failedCount === 1 ? 'foi ignorada' : 'foram ignoradas'}.`,
    truncatedCapacity: ({ n }: { n: number }) =>
      `Rastreou ${n} página${n === 1 ? '' : 's'} antes da nossa própria capacidade de rastreamento acabar. Isso é temporário do nosso lado — espere alguns minutos e rode o rastreamento de novo.`,
    truncatedDeadline: ({ n }: { n: number }) =>
      `Rastreou ${n} página${n === 1 ? '' : 's'} antes deste rastreamento atingir nosso próprio limite de tempo.`,
    truncatedAttempts: ({ n }: { n: number }) =>
      `Rastreou ${n} página${n === 1 ? '' : 's'} antes do rastreamento ficar sem tentativas em páginas que não carregavam.`,
    truncatedPageCap: ({ n }: { n: number }) =>
      `Rastreou ${n} página${n === 1 ? '' : 's'} — o máximo que o limite de páginas do seu plano permite agora.`,
    remainingAtLeast: ({ n }: { n: number }) =>
      ` Pelo menos ${n} página${n === 1 ? '' : 's'} não ${n === 1 ? 'foi indexada' : 'foram indexadas'}.`,
    remainingNone: ' Algumas páginas podem não ter sido indexadas.',
    crawlFailedGeneric: "Este rastreamento não indexou nenhuma página.",

    typeUrl: 'URL',
    typeSitemap: 'Sitemap',
    typeText: 'Texto',
    typeFile: 'Arquivo',

    languagePrefix: 'Idioma: ',

    factType: 'Tipo',
    factRenderedMode: 'Modo renderizado',
    factLastSynced: 'Última sincronização',

    resyncCta: 'Ressincronizar',
    deleteCta: 'Excluir',

    latestCrawlHeading: 'Último rastreamento',
    neverCrawledYet: "Esta fonte ainda não foi rastreada.",
    crawlRanInMode: ({ rendered }: { rendered: boolean }) =>
      `Este rastreamento rodou no modo ${rendered ? 'renderizado' : 'padrão'}.`,

    failedUrlsHeading: 'URLs com falha',
    failedUrlsCountCaveat: ({ total, shown }: { total: number; shown: number }) =>
      ` — ${total} no total, mostrando as primeiras ${shown}`,
    offSiteRedirectsHeading: 'Redirecionamentos para fora do site',
    offSiteRedirectsMoreCaveat: ' — pode haver mais do que esta lista mostra',
    nearEmptyCameBack: ({ n }: { n: number }) =>
      `${n} página${n === 1 ? '' : 's'} ${n === 1 ? 'voltou' : 'voltaram'} com quase nenhum conteúdo.`,

    pagesIndexedHeading: 'Páginas indexadas',
    nearEmptyOnlyToggle: 'Só quase vazias',

    noPagesIndexedYet: 'Nenhuma página indexada ainda.',
    colUrl: 'URL',
    colCharacters: 'Caracteres',
    colStatus: 'Status',
    nearEmptyBadge: 'quase vazia',

    indexStateIndexed: 'Indexada',
    indexStateIndexing: 'Indexando…',
    indexStateFailed: 'Falhou',

    fileNoMarkdownExplainer:
      'Guardado como o próprio arquivo enviado — mantemos o original, não uma cópia separada em texto, então não há nada para pré-visualizar aqui. O arquivo está indexado e o bot responde a partir dele.',
    nothingStoredForDocument: 'Nada guardado para este documento.',

    storedContentHeading: 'Conteúdo guardado',
    nothingStoredYet: 'Nada guardado ainda.',

    deleteDialogTitle: 'Excluir esta fonte?',
    deleteDialogDescriptionPrefix: 'Remove ',
    deleteDialogDescriptionSuffix: ' e tudo que ela indexou. Isso não pode ser desfeito.',
    cancelCta: 'Cancelar',
  },
  conversations: {
    today: 'Hoje',
    yesterday: 'Ontem',
    yourTesting: 'Seus testes',
    visitor: 'Visitante',
    playgroundTag: 'Playground',
    noMessageFallback: '(sem mensagem)',
    leadCapturedTag: 'Lead capturado',
    hasUnansweredTag: 'Tem pergunta sem resposta',
    unansweredChipLabel: 'Sem resposta',
    loadingMore: 'Carregando mais…',
    loadMore: 'Carregar mais',
    selectConversationPrompt: 'Selecione uma conversa para ver a transcrição.',
    backToConversations: '← Voltar para conversas',
    transcriptStartedSummary: ({ time, n }: { time: string; n: number }) =>
      `Iniciada ${time} · ${n} mensagens`,
    deleteConversationAriaLabel: 'Excluir esta conversa',
    leadSubmittedTag: '✓ Lead enviado',
    cancelCta: 'Cancelar',
    deletingCta: 'Excluindo…',

    pageTitle: 'Conversas',
    headerSubtitle: ({ n }: { n: number }) =>
      `${n} ${n === 1 ? 'conversa' : 'conversas'} · mais recentes primeiro`,
    emptyTitle: 'Esperando seu primeiro visitante',
    emptyDescription: 'As conversas aparecem aqui assim que alguém conversar com o seu bot.',

    deleteAllCta: 'Excluir todas as conversas',
    deleteDialogTitle: 'Excluir esta conversa?',
    deleteDialogDescription:
      'Exclui permanentemente esta transcrição e suas mensagens. Um lead capturado nela continua na aba Leads. Isso não pode ser desfeito.',
    deleteCta: 'Excluir conversa',

    deleteAllDialogTitle: 'Excluir todas as conversas?',
    deleteAllDescriptionPrefix:
      'Exclui permanentemente todas as conversas e mensagens deste bot. Os leads capturados continuam na aba Leads. Isso não pode ser desfeito. Digite ',
    deleteAllDescriptionSuffix: ' para confirmar.',
    confirmBotNameAriaLabel: 'Confirmar nome do bot',
  },
  leads: {
    filterNew: 'Novos',
    filterContacted: 'Contatados',
    filterArchived: 'Arquivados',
    filterAll: 'Todos',
    actionMarkNew: 'Marcar como novo',
    actionMarkContacted: 'Marcar como contatado',
    actionArchive: 'Arquivar',
    statusNew: 'novo',
    statusContacted: 'contatado',
    statusArchived: 'arquivado',

    filterByStatusAriaLabel: 'Filtrar por status',
    pageTitle: 'Leads',
    exportingCta: 'Exportando…',
    exportCsvCta: 'Exportar CSV',
    exportCsvError: 'Não foi possível exportar os leads — tente novamente.',

    headerSubtitleAll: ({ n }: { n: number }) =>
      `${n} ${n === 1 ? 'lead' : 'leads'} capturados · mais recentes primeiro`,
    headerSubtitleFiltered: ({ n, filterLabel }: { n: number; filterLabel: string }) =>
      `${n} lead${n === 1 ? '' : 's'} ${filterLabel} · mais recentes primeiro`,

    deleteAllCta: 'Excluir todos os leads',
    bulkActionsAriaLabel: 'Ações em massa',
    selectedCount: ({ n }: { n: number }) => `${n} selecionados`,
    bulkCapMessage: ({ max }: { max: number }) => `Selecione até ${max} leads por vez`,
    bulkUpdatedMessage: ({ n }: { n: number }) => `${n} atualizados`,
    bulkUpdateError: "Não foi possível atualizar esses leads. Tente novamente.",

    emptyTitle: 'Esperando seu primeiro lead',
    emptyDescription: "Os leads capturados pelo widget de chat do seu bot vão aparecer aqui.",
    noFilterResultsTitle: ({ filterLabel }: { filterLabel: string }) => `Nenhum lead ${filterLabel}`,
    noFilterResultsDescription: 'Nada neste filtro no momento.',

    selectAllAriaLabel: 'Selecionar todos os leads',
    colName: 'Nome',
    colContact: 'Contato',
    colIntent: 'Intenção',
    colStatus: 'Status',
    colCaptured: 'Capturado',
    colActions: 'Ações',

    noEmailFallback: '(sem e-mail)',
    selectLeadAriaLabel: ({ contact }: { contact: string }) => `Selecionar lead ${contact}`,
    deleteLeadAriaLabel: ({ contact }: { contact: string }) => `Excluir lead ${contact}`,
    playgroundTag: 'Playground',
    viewChatCta: 'Ver conversa',
    loadingMoreCta: 'Carregando mais…',
    loadMoreCta: 'Carregar mais',

    cancelCta: 'Cancelar',
    deletingCta: 'Excluindo…',

    deleteDialogTitle: 'Excluir este lead?',
    deleteDescriptionPrefix: 'Exclui permanentemente ',
    deleteDescriptionSuffix: '. Isso não pode ser desfeito.',
    noContactFallback: 'este lead',
    deleteCta: 'Excluir lead',

    deleteAllDialogTitle: 'Excluir todos os leads?',
    deleteAllDescriptionPrefix:
      'Exclui permanentemente todos os leads capturados por este bot. Isso não pode ser desfeito. Digite ',
    deleteAllDescriptionSuffix: ' para confirmar.',
    confirmBotNameAriaLabel: 'Confirmar nome do bot',
  },
  unanswered: {
    tabOpen: 'Em aberto',
    tabDismissed: 'Dispensadas',
    tabAnswered: 'Respondidas',
    filterByStatusAriaLabel: 'Filtrar por status',

    pageTitle: 'Sem resposta',
    headerSubtitle: ({ n, verb }: { n: number; verb: string }) =>
      `${n} pergunta${n === 1 ? '' : 's'} ${verb}`,
    subtitleVerbOpen: 'aguardando resposta',
    subtitleVerbDismissed: 'em descarte',
    subtitleVerbAnswered: 'com resposta',

    openTabIntro:
      "Perguntas que seu bot não conseguiu responder, agrupadas. Escreva uma resposta uma vez — ela vira uma fonte de P&R e o bot sabe disso para sempre.",

    emptyTitleOpen: "Nada em aberto — seu bot respondeu tudo o que foi perguntado.",
    emptyTitleDismissed: 'Nada descartado.',
    emptyTitleAnswered: 'Nenhuma pergunta respondida ainda.',

    askedOnce: 'Perguntado uma vez',
    askedTimes: ({ n }: { n: number }) => `Perguntado ${n} vezes`,

    viewSourceCta: 'Ver fonte',
    sourceRemoved: 'fonte removida',
    viewChatCta: 'Ver conversa',
    chatDeleted: 'conversa excluída',

    dismissCta: 'Descartar',
    reopenCta: 'Reabrir',
    writeAnswerCta: 'Escrever resposta',
    cancelCta: 'Cancelar',
    answerPlaceholder: "Escreva a resposta que você daria a este visitante. O bot vai usá-la a partir de agora.",
    addingCta: 'Adicionando…',
    addAsQaSourceCta: 'Adicionar como fonte de P&R',
    answerAddedNotice: 'Adicionada como fonte de P&R — o bot vai usá-la a partir da próxima conversa.',
    saveAnswerError: "Não foi possível salvar essa resposta — tente novamente.",
  },
  appearance: {
    pageTitle: 'Aparência',
    pageSubtitle: 'Como o widget aparece e saúda os visitantes no seu site',

    colorLabel: 'Cor',
    colorHexAriaLabel: 'Valor hexadecimal da cor',
    botNameLabel: 'Nome do bot',
    botNamePlaceholder: 'Suporte Acme',
    welcomeMessageLabel: 'Mensagem de boas-vindas',
    welcomePlaceholder: 'Oi! Como posso ajudar você hoje?',

    quickQuestionsLabel: 'Perguntas rápidas',
    addQuestionCta: 'Adicionar pergunta',
    quickQuestionsHint: "Opcional — aparecem como chips que os visitantes podem tocar antes de digitar qualquer coisa.",
    quickQuestionAriaLabel: ({ n }: { n: number }) => `Pergunta rápida ${n}`,
    quickQuestionPlaceholder: 'Qual é o horário de funcionamento de vocês?',
    removeCta: 'Remover',

    positionLabel: 'Posição do lançador',
    positionBottomRight: 'Inferior direito',
    positionBottomLeft: 'Inferior esquerdo',

    languageLabel: 'Idioma',
    autoLanguageLabel: "Seguir o navegador do visitante",

    badgeLabel: 'Selo "Powered by Embent"',
    badgeHint: 'Mostra um pequeno link "Powered by Embent" no rodapé do widget.',
    badgeSwitchAriaLabel: 'Mostrar o selo "Powered by Embent"',
    badgeLoadError: "Não foi possível carregar — atualize a página",
    freeBadgeNote: ({ plans }: { plans: string }) => `Fazer upgrade para ${plans} remove essa exigência.`,

    saveCta: 'Salvar',
    savingCta: 'Salvando…',
    savedLabel: 'Salvo',

    livePreviewHeading: 'Prévia ao vivo',
    previewCaption:
      'Só o botão lançador e o painel renderizam aqui — a página ao redor fica em branco de propósito.',
    devHintPrefix: "Se nada aparecer, o pacote do widget não está compilado localmente: rode",
    devHintIn: 'em',
    devHintAndCopy: 'e copie',
    devHintTo: 'para',

    widgetPreviewTitle: 'Prévia do widget',
  },
  botSettings: {
    pageTitle: 'Configurações',
    pageSubtitle: 'Domínios permitidos, captura de leads e como o bot se apresenta',

    leadCaptureOffLabel: 'Desligado',
    leadCaptureOffBlurb: 'O bot nunca pede detalhes de contato aos visitantes.',
    leadCaptureOnIntentLabel: 'Sob intenção',
    leadCaptureOnIntentBlurb:
      "O bot pede um e-mail ou telefone só quando não consegue responder ou o visitante quer um retorno.",
    leadCaptureAlwaysLabel: 'Sempre',
    leadCaptureAlwaysBlurb:
      'O bot pede os detalhes de contato uma vez, logo após a primeira troca na conversa.',
    leadCapturePreChatLabel: 'Formulário antes do chat',
    leadCapturePreChatBlurb:
      'Os visitantes preenchem um formulário curto de contato (nome, e-mail, telefone opcional) antes do chat começar. O bot nunca pergunta de novo durante a conversa.',

    leadLevelSubtleLabel: 'Discreto',
    leadLevelSubtleBlurb: 'Só oferece o retorno quando o visitante pede para ser contatado ou quer um orçamento.',
    leadLevelBalancedLabel: 'Equilibrado (recomendado)',
    leadLevelBalancedBlurb:
      'Oferece uma vez quando o visitante mostra sinais reais de compra — pergunta como começar, quer uma demonstração, ou menciona o próprio site.',
    leadLevelProactiveLabel: 'Proativo',
    leadLevelProactiveBlurb:
      'Oferece sempre que o visitante parece estar avaliando, e pergunta mais uma vez depois se a primeira oferta ficou sem resposta.',

    domainRequiredError: 'Obrigatório — remova a linha em vez de deixá-la em branco',
    domainBareHostnameError: 'Só o nome do domínio, ex.: "exemplo.com" (sem protocolo ou caminho)',
    domainTooLongError: 'Muito longo',

    aboutLabel: 'Sobre este bot',
    aboutHint:
      'Uma breve apresentação que o bot sempre conhece — quem ele é, quem ele atende — para que ' +
      'perguntas como "quem é você?" sejam respondidas na hora, sem precisar buscar.',
    aboutPlaceholder:
      "Somos o assistente de suporte da Acme, aqui para ajudar clientes a configurar e resolver problemas com os Widgets Acme.",

    allowedDomainsLabel: 'Domínios permitidos',
    addDomainCta: 'Adicionar domínio',
    allowedDomainsHint:
      "Sites autorizados a incorporar o widget deste bot. Deixe vazio para permitir qualquer site — " +
      'recomendamos restringir ao(s) seu(s) próprio(s) domínio(s) antes de entrar no ar.',
    domainAriaLabel: ({ n }: { n: number }) => `Domínio permitido ${n}`,
    domainPlaceholder: 'example.com',
    removeCta: 'Remover',

    leadCaptureLabel: 'Captura de leads',
    leadLevelLabel: 'Quão proativo?',

    saveCta: 'Salvar',
    savingCta: 'Salvando…',
    savedLabel: 'Salvo',

    embedSnippetHeading: 'Código de incorporação',
    embedPastePrefix: 'Cole isto antes do fechamento',
    embedPasteSuffix: 'no seu site.',

    embedCopyCta: 'Copiar',
    embedCopiedCard: 'Copiado!',
    embedCopiedCheck: '✓ Copiado',

    dangerZoneHeading: 'Zona de risco',
    dangerZoneDescription:
      'Exclui o bot, suas fontes, conversas, leads e seu índice de busca. Isso não pode ser desfeito.',
    deleteBotCta: 'Excluir este bot',
    deleteBotDialogTitle: ({ name }: { name: string }) => `Excluir "${name}"?`,
    dangerZoneDialogDescriptionPrefix:
      'Exclui o bot, suas fontes, conversas, leads e seu índice de busca. Isso não pode ser desfeito. Digite ',
    dangerZoneDialogDescriptionSuffix: ' para confirmar.',
    confirmBotNameAriaLabel: 'Confirmar nome do bot',
    cancelCta: 'Cancelar',
    deletingCta: 'Excluindo…',
  },
  notifications: {
    heading: 'Notificações',
    subheading: "Os e-mails vão para o endereço de conta de cada pessoa. A atividade no Playground nunca dispara e-mails.",
    noMembers: 'Nenhum membro.',
    saveError: "Não foi possível salvar essa configuração. Tente novamente.",

    roleOwner: 'Proprietário',
    roleAdmin: 'Admin',
    roleMember: 'Membro',
    cadenceOff: 'Desligado',
    cadenceDaily: 'Diário',
    cadenceWeekly: 'Semanal',

    colMember: 'Membro',
    colNewLead: 'Novo lead',
    colUnansweredDigest: 'Resumo de sem resposta',
    colCrawlFailed: 'Falha no rastreamento',
    colWeeklySummary: 'Resumo semanal',

    newLeadAriaLabel: ({ email }: { email: string }) => `E-mails de novo lead para ${email}`,
    unansweredDigestAriaLabel: ({ email }: { email: string }) => `E-mails de resumo de sem resposta para ${email}`,
    crawlFailedAriaLabel: ({ email }: { email: string }) => `E-mails de falha no rastreamento para ${email}`,
    weeklySummaryAriaLabel: ({ email }: { email: string }) => `E-mails de resumo semanal para ${email}`,
  },
  playground: {
    pageTitle: 'Playground',
    pageSubtitle: "Converse com o seu bot como um visitante faria — nada aqui chega até o seu site.",
    retrievalDebugLabel: 'Debug de busca',
    hideDebugCta: 'Esconder debug',
    newSessionCta: 'Nova sessão',
    emptyStateText: 'Envie uma mensagem para testar como este bot responde, com base nas suas fontes.',
    inputPlaceholder: 'Faça uma pergunta a este bot…',
    stopCta: 'Parar',
    sendCta: 'Enviar',
    debugEmptyText: 'Faça uma pergunta para ver o que ele busca',
    genericError: 'Algo deu errado.',
  },
  billing: {
    pageTitle: 'Cobrança',
    openingCta: 'Abrindo…',
    manageBillingCta: 'Gerenciar cobrança',

    memberGateTitle: 'A cobrança é gerenciada pelo proprietário ou um admin do seu workspace.',
    memberGateDescription: "Peça a um proprietário ou admin para fazer mudanças no plano deste workspace.",

    nowOnPlan: ({ name }: { name: string }) => `Você agora está no plano ${name}.`,
    finishingUp: "Estamos finalizando isso — atualize a página em instantes se o seu plano não mudar.",
    confirmingWithPolar: 'Mudança recebida — confirmando com a Polar…',

    internalWorkspaceLabel: 'Workspace interno',
    monthlyPriceLabel: ({ price }: { price: number }) => `$${price} / mês`,
    internalWorkspaceInline: 'Sem limites, cobrança não se aplica.',
    internalWorkspaceHeading: 'Workspace interno — sem limites, cobrança não se aplica.',
    internalWorkspaceBody:
      'Todo limite de plano é ignorado e não há nada para fazer upgrade ou checkout aqui. O uso é ' +
      'registrado acima para visibilidade, mas nunca é enviado para a Polar — este workspace ' +
      'não tem identidade de cobrança.',

    unlimitedWord: 'ilimitado',
    unlimitedParens: '(ilimitado)',
    ofCap: ({ cap }: { cap: string }) => `/ ${cap}`,
    internalRenewalFallback: 'Workspace interno · sem limites · cobrança não se aplica',
    capsSuffix: ({
      bots,
      botsCount,
      messages,
      pages,
    }: {
      bots: string;
      botsCount: number | null;
      messages: string;
      pages: string;
    }) => `${bots} bot${botsCount === 1 ? '' : 's'} · ${messages} mensagens · ${pages} páginas`,
    freeForever: ({ capsSuffix }: { capsSuffix: string }) => `Grátis para sempre · ${capsSuffix}`,
    renewsOn: ({ date, capsSuffix }: { date: string; capsSuffix: string }) =>
      `Renova em ${date} · ${capsSuffix}`,

    cancelsAtPeriodEnd: 'Cancela no fim do período',
    planChangesPausedNotice: 'Mudanças de plano estão pausadas até o fim do período atual.',
    planChangesPausedReason:
      'Esta assinatura está configurada para cancelar no fim do período — reative-a primeiro para mudar de plano.',

    redirectingCta: 'Redirecionando…',
    switchingCta: 'Trocando…',
    upgradeToCta: ({ name }: { name: string }) => `Fazer upgrade para ${name}`,

    messagesThisMonthLabel: 'Mensagens este mês',
    pagesIndexedLabel: 'Páginas indexadas',
    messageNoun: 'mensagem',
    pageNoun: 'página',
    messagesUnit: ({ n }: { n: string }) => `${n} mensagens`,
    usageLimitWarning: ({ pct, noun }: { pct: number; noun: string }) =>
      `${pct}% do seu limite de ${noun}`,
    upgradeRaisesTo: ({ name, unit }: { name: string; unit: string }) =>
      ` — ${name} aumenta para ${unit}.`,
    limitReached: ' atingido.',

    cancelSubscriptionCta: 'Cancelar assinatura',
    cancelingCta: 'Cancelando…',
    cancelDialogTitle: 'Cancelar assinatura?',
    yourCurrentPlanFallback: 'seu plano atual',
    cancelDialogDescription: ({
      planName,
      renewsDate,
    }: {
      planName: string;
      renewsDate: string | null;
    }) =>
      `Seus bots continuam funcionando no ${planName} até o fim deste período de cobrança${
        renewsDate ? ` (${renewsDate})` : ''
      } — depois o workspace cai para o Free. Isso nunca acontece imediatamente.`,
    keepSubscriptionCta: 'Manter assinatura',

    changePlanFallbackTitle: 'Mudar de plano?',
    keepCurrentPlanCta: 'Manter plano atual',
    confirmFallbackCta: 'Confirmar',

    checkoutErrorFallback: 'Algo deu errado ao iniciar o checkout.',
    portalErrorFallback: 'Algo deu errado ao abrir o gerenciamento de cobrança.',
    cancelErrorFallback: 'Algo deu errado ao cancelar sua assinatura.',

    currentPlanBadge: 'Plano atual',
    botsUnit: ({ n }: { n: number }) => `${n} bot${n === 1 ? '' : 's'}`,
    seatsUnit: ({ n }: { n: number }) => `${n} assento${n === 1 ? '' : 's'}`,
    messagesPerMonth: ({ n }: { n: string }) => `${n} mensagens / mês`,
    pagesUnit: ({ n }: { n: string }) => `${n} páginas`,
    badgeRequiredText: 'Selo "Powered by Embent"',
    badgeOptionalText: 'Selo "Powered by Embent" opcional',
    upgradeCta: 'Fazer upgrade',
    switchToCta: ({ name }: { name: string }) => `Trocar para ${name}`,

    flatPricingNote:
      'Preço fixo, sem cálculo por usuário, sem surpresas na fatura — avisamos você em 80% e pausamos novas ' +
      'respostas só depois de 110%, e nunca cortamos uma conversa no meio de uma resposta.',

    planChangeConfirm: {
      verbUpgrade: 'Fazer upgrade',
      verbSwitch: 'Trocar',
      title: ({ verb, name }: { verb: string; name: string }) => `${verb} para ${name}?`,
      confirmNow: ({ verb }: { verb: string }) => `${verb} agora`,
      continueToPolarCta: 'Continuar para a Polar',
      checkoutDescription: ({ name, price }: { name: string; price: number }) =>
        `Você vai ser levado para a Polar para começar uma assinatura ${name} ($${price}/mês) — a Polar ` +
        'vai cobrar você lá para ativá-la.',
      switchDescription: ({ name, price }: { name: string; price: number }) =>
        `Isso troca seu plano imediatamente — seus bots passam a usar os limites do ${name} ` +
        `($${price}/mês) agora mesmo. A Polar não vai cobrar você hoje: ela aplica a diferença ` +
        'proporcional na sua próxima fatura.',
    },
  },
  workspace: {
    pageTitle: 'Configurações do workspace',
    loadErrorMessage: "Não foi possível carregar este workspace. Recarregue a página para tentar de novo.",
    notAllowedTitle: 'Só proprietários e admins podem mudar as configurações do workspace.',
    notAllowedDescription: 'Peça a um proprietário ou admin deste workspace para fazer mudanças.',

    nameLabel: 'Nome do workspace',
    nameHint: ({ min, max }: { min: number; max: number }) =>
      `${min}–${max} caracteres. Mostrado para todos neste workspace e nos e-mails de convite.`,

    uploadLogoCta: 'Enviar logo',
    removeLogoCta: 'Remover',
    logoHint: 'Quadrado funciona melhor. PNG, JPEG, WebP ou GIF, até 2 MB.',
    logoNotConfiguredError: "O envio de logo não está configurado neste ambiente.",
    uploadLogoErrorFallback: "Não foi possível enviar o logo. Tente novamente.",
    removeLogoErrorFallback: "Não foi possível remover o logo. Tente novamente.",

    saveNameErrorFallback: "Não foi possível salvar o nome. Tente novamente.",
    saveCta: 'Salvar',
    savedLabel: 'Salvo',

    cropDialogTitle: 'Recorte seu logo',
    cropDialogDescription: 'Arraste para reposicionar, use o zoom para ajustar. Salvo como um quadrado de 256×256.',
    zoomLabel: 'Zoom',
    cropErrorFallback: "Não foi possível recortar essa imagem. Tente outro arquivo.",
    cancelCta: 'Cancelar',
    useThisLogoCta: 'Usar este logo',
  },
  members: {
    pageTitle: 'Membros',
    membersOnlyTitle: 'Área só para membros',
    membersOnlyDescription: 'Peça ao proprietário ou a um admin do seu workspace para gerenciar membros.',
    unlimitedCapWord: 'Ilimitado',
    seatsSubtitle: ({
      used,
      cap,
      pending,
    }: {
      used: number;
      cap: string | number;
      pending: number;
    }) => `${used} de ${cap} assentos usados${pending > 0 ? ` (${pending} convidados)` : ''}`,
    inviteMemberCta: 'Convidar membro',

    roleOwner: 'Proprietário',
    roleAdmin: 'Admin',
    roleMember: 'Membro',
    roleOptionMember: 'Membro',
    roleOptionAdmin: 'Admin',

    colName: 'Nome',
    colEmail: 'E-mail',
    colRole: 'Função',
    leaveWorkspaceCta: 'Sair do workspace',
    removeCta: 'Remover',

    pendingInvitesHeading: 'Convites pendentes',
    noPendingInvites: 'Nenhum convite pendente.',
    inviteColEmail: 'E-mail',
    inviteColRole: 'Função',
    inviteColExpires: 'Expira',
    revokingCta: 'Revogando…',
    revokeCta: 'Revogar',

    cancelCta: 'Cancelar',
    removingCta: 'Removendo…',
    leaveDialogTitle: 'Sair deste workspace?',
    removeDialogTitle: ({ name }: { name: string }) => `Remover ${name}?`,
    leaveDialogDescription: "Você vai perder o acesso imediatamente — um proprietário ou admin pode te convidar de volta depois.",
    removeDialogDescription: ({ name }: { name: string }) => `${name} perde o acesso a este workspace imediatamente.`,

    emailLabel: 'E-mail',
    emailPlaceholder: 'teammate@example.com',
    roleLabel: 'Função',
    inviteErrorFallback: 'Algo deu errado ao enviar este convite.',
    sendingInviteCta: 'Enviando…',
    sendInviteCta: 'Enviar convite',

    roleChangeErrorFallback: 'Não foi possível mudar essa função.',
    removeErrorFallback: 'Não foi possível remover este membro.',
    revokeErrorFallback: 'Não foi possível revogar este convite.',
  },
  account: {
    pageTitle: 'Conta',
    loadSignInMethodsError: 'Não foi possível carregar os métodos de login',
    emailPasswordLabel: 'E-mail e senha',
    googleLabel: 'Google',

    savingCta: 'Salvando…',

    profileHeading: 'Perfil',
    displayNameLabel: 'Nome de exibição',
    namePlaceholder: 'Seu nome',
    emailLabel: 'E-mail',
    saveNameErrorFallback: 'Algo deu errado ao salvar seu nome.',
    saveCta: 'Salvar',
    savedLabel: 'Salvo',

    passwordHeading: 'Senha',
    passwordMismatchError: 'A nova senha e a confirmação não coincidem.',
    currentPasswordLabel: 'Senha atual',
    newPasswordLabel: 'Nova senha',
    confirmNewPasswordLabel: 'Confirmar nova senha',
    changePasswordErrorFallback: 'Algo deu errado ao mudar sua senha.',
    changePasswordCta: 'Mudar senha',
    passwordChangedLabel: 'Senha alterada',
  },
  adminImpersonate: {
    impersonateFailedFallback: 'Não foi possível se passar por este usuário.',
    lookupFailedFallback: 'Não foi possível buscar este usuário.',
    userNotFound: 'Conta de usuário não encontrada.',
    noOwnerToView: 'Este workspace não tem um proprietário para visualizar como.',
  },
  admin: {
    pagination: {
      pageOfTotal: ({
        page,
        totalPages,
        totalCount,
      }: {
        page: number;
        totalPages: number;
        totalCount: string;
      }) => `Página ${page} de ${totalPages} · ${totalCount} no total`,
      prevCta: 'Anterior',
      nextCta: 'Próximo',
    },
    workspaces: {
      crawlSignalRenderedLabel: 'Depende de rastreamento em modo renderizado',
      crawlSignalSourcesAcrossBots: ({
        sourceCount,
        botCountLabel,
        botCount,
      }: {
        sourceCount: number;
        botCountLabel: string;
        botCount: number;
      }) =>
        `fonte${sourceCount === 1 ? '' : 's'} em ${botCountLabel} bot${botCount === 1 ? '' : 's'}`,
      crawlSignalRenderedFootnote:
        'Quanto da frota depende do Cloudflare Browser Rendering — capacidade que temos no plano Workers Free hoje, não no Paid.',
      crawlSignalCapacityLabel: 'Atingiu nosso próprio limite de capacidade de renderização (7d)',
      crawlSignalSourceCountLabel: ({ n }: { n: number }) => `fonte${n === 1 ? '' : 's'}`,
      crawlSignalCapacityFootnote:
        "Fontes distintas, só a nossa própria capacidade — nunca o limite de plano de um cliente. Um processo morto ou um job obsoleto descartado não deixa registro — isso é um piso. Uma alta constante significa que está na hora do Workers Paid ($5/mês).",

      emptyText: 'Nenhum workspace ainda.',
      internalBadge: 'INTERNO',
      cancelsAtPeriodEndHint: 'cancela no fim do período',
      colName: 'Nome',
      colOwner: 'Proprietário',
      colPlan: 'Plano',
      colBots: 'Bots',
      colMembers: 'Membros',
      colMsgsPerMo: 'Msgs / mês',
      colPages: 'Páginas',
      colCreated: 'Criado',
      colActions: 'Ações',
      noOwnerTitle: 'Este workspace não tem um proprietário',
      viewAsCta: 'Ver como',
      switchingCta: 'Trocando…',
      viewAsErrorFallback: 'Falhou',

      backToWorkspaces: '← Voltar para Workspaces',
      ownerLabel: 'Proprietário: ',
      bannedBadge: 'banido',
      createdLabel: ({ date }: { date: string }) => `Criado ${date}`,
      usageHeading: 'Uso',
      messagesThisMonthLabel: 'Mensagens este mês',
      pagesIndexedLabel: 'Páginas indexadas',
      botsLabel: 'Bots',
      internalUsageNote: 'Workspace interno — nenhum limite se aplica; o uso continua sendo registrado acima.',
      botsHeading: 'Bots',
      noBotsYet: 'Nenhum bot ainda.',
      colBotName: 'Nome',
      colPublicId: 'ID público',
      colStatus: 'Status',
      colBotCreated: 'Criado',
      membersHeading: 'Membros',
      noMembersYet: 'Nenhum membro.',
      colEmail: 'E-mail',
      colMemberName: 'Nome',
      colRole: 'Função',
      colJoined: 'Entrou',
      memberRoleLabel: {
        owner: 'Proprietário',
        admin: 'Admin',
        member: 'Membro',
      },
      subscriptionHeading: 'Assinatura',
      statusLabel: 'Status: ',
      periodEndLabel: 'Fim do período: ',
      cancelsAtPeriodEndLabel: 'Cancela no fim do período: ',
      yesLabel: 'Sim',
      noLabel: 'Não',
      polarSubscriptionIdLabel: 'ID da assinatura na Polar: ',
      manageInPolarLink: 'Gerenciar na Polar ↗',
      noSubscriptionText: 'Nenhuma assinatura — este workspace está no plano gratuito.',
      dangerZoneHeading: 'Zona de risco',
      internalToggleLabel: 'Workspace interno',
      internalToggleHint: 'Sem limites, sem cobrança — o uso continua sendo registrado, mas nunca é enviado para a Polar.',
      banOwnerLabel: 'Banir proprietário',
      banOwnerHint: 'Usuários banidos não conseguem entrar. O widget/API pública não são afetados.',
      unbanCta: 'Desbanir',
      unbanningCta: 'Desbanindo…',
      banCta: 'Banir',
      demoteBeforeBanTitle: 'Rebaixe este admin para usuário antes de baní-lo.',
      roleLabel: 'Função',
      roleHint: 'Admins podem acessar toda tela administrativa interna.',
      cannotChangeOwnRoleTitle: 'Você não pode mudar sua própria função.',
      cancelCta: 'Cancelar',
      savingCta: 'Salvando…',
      confirmCta: 'Confirmar',
      markInternalDialogTitle: 'Marcar este workspace como interno?',
      removeInternalDialogTitle: 'Remover status interno?',
      markInternalDialogDescription:
        'Este workspace vai ignorar todo limite de plano (bots, mensagens, páginas) e nunca será cobrado. O uso continua sendo registrado para visibilidade, mas o envio noturno de uso para a Polar o ignora — não há identidade de cobrança para enviar.',
      removeInternalDialogDescription:
        'Este workspace volta para a aplicação normal de plano e cobrança (se tiver uma assinatura ativa).',
      banDialogTitle: ({ email }: { email: string | undefined }) => `Banir ${email}?`,
      banDialogDescription: 'Isso desconecta a pessoa imediatamente e impede qualquer login futuro até ser desbanida.',
      banReasonLabel: 'Motivo (opcional)',
      banReasonPlaceholder: 'Por que esta conta está sendo banida?',
      banningCta: 'Banindo…',
      banUserCta: 'Banir usuário',
      roleDialogTitle: ({ role }: { role: 'user' | 'admin' | null }) => `Mudar função para ${role}?`,
      roleDialogDescriptionAdmin: ({ email }: { email: string | undefined }) =>
        `${email} vai ganhar acesso a toda tela administrativa interna.`,
      roleDialogDescriptionUser: ({ email }: { email: string | undefined }) =>
        `${email} vai perder o acesso às telas administrativas internas.`,
      updateWorkspaceErrorFallback: 'Não foi possível atualizar este workspace.',
      banUserErrorFallback: 'Não foi possível banir este usuário.',
      unbanUserErrorFallback: 'Não foi possível desbanir este usuário.',
      changeRoleErrorFallback: 'Não foi possível mudar a função deste usuário.',
    },
    bots: {
      colName: 'Nome',
      colPublicId: 'ID público',
      colWorkspace: 'Workspace',
      colStatus: 'Status',
      colSources: 'Fontes',
      colLastCrawl: 'Último rastreamento',
      colInstanceId: 'ID da instância',
      noBotsYet: 'Nenhum bot ainda.',

      backToBots: '← Voltar para Bots',
      workspaceLabel: 'Workspace: ',
      instanceLabel: ({ id }: { id: string }) => `Instância: ${id}`,
      createdLabel: ({ date }: { date: string }) => `Criado ${date}`,
      openOwnerViewCta: "Abrir visão do proprietário",
      openingCta: 'Abrindo…',
      openOwnerViewErrorFallback: 'Não foi possível abrir a visão do proprietário.',
      sourcesHeading: 'Fontes',
      noSourcesYet: 'Nenhuma fonte ainda.',
      colUrl: 'URL',
      colType: 'Tipo',
      colSourceStatus: 'Status',
      colPages: 'Páginas',
      colLastCrawled: 'Último rastreamento',
      colError: 'Erro',
      recentJobsHeading: 'Jobs recentes',
      noJobsLinked: 'Nenhum job vinculado a este bot ainda.',
      colKind: 'Tipo',
      colJobStatus: 'Status',
      colAttempts: 'Tentativas',
      colUpdated: 'Atualizado',
      colPayload: 'Payload',
      recentJobsFootnote:
        "Os jobs recentes são correspondidos da melhor forma possível pelo payload de cada job — um job que não carrega o id deste bot ou de uma das suas fontes não vai aparecer aqui, mesmo que tenha afetado este bot indiretamente.",
    },
    jobs: {
      tabLabel: {
        all: 'Todos',
        running: 'Em execução',
        failed: 'Com falha',
      },
      noFailedJobs: 'Nenhum job com falha — a fila está saudável.',
      noJobsToShow: 'Nenhum job para mostrar.',
      colKind: 'Tipo',
      colStatus: 'Status',
      colPayload: 'Payload',
      colAttempts: 'Tentativas',
      colCreated: 'Criado',
      colUpdated: 'Atualizado',
      colActions: 'Ações',
      retryCta: 'Tentar de novo',
      retryingCta: 'Tentando de novo…',
      cancelCta: 'Cancelar',
      cancellingCta: 'Cancelando…',
      cancelJobCta: 'Cancelar job',
      keepJobCta: 'Manter job',
      cancelDialogTitle: 'Cancelar este job?',
      retryJobErrorFallback: 'Não foi possível tentar este job de novo.',
      cancelJobErrorFallback: 'Não foi possível cancelar este job.',
      cancelDialogDescriptionListRow:
        "Isso exclui a linha do job pendente — ele nunca vai rodar. Só jobs pendentes podem ser cancelados assim; o processo de um job em execução pode já estar em andamento nele.",

      backToJobs: '← Voltar para Jobs',
      jobIdLabel: 'ID do job',
      attemptsLabel: 'Tentativas',
      createdLabel: 'Criado',
      updatedLabel: 'Atualizado',
      runAfterLabel: 'Executar após',
      errorHeading: 'Erro',
      payloadHeading: 'Payload',
      cancelDialogDescriptionDetailPage: 'Isso exclui a linha do job pendente — ele nunca vai rodar.',
    },
    posts: {
      description:
        'Posts do blog, rascunhos e publicados. Editar aqui entra no ar em embent.com em até um minuto após publicar.',
      newPostCta: 'Novo post',
      emptyText: 'Nenhum post ainda — crie o primeiro.',
      colTitle: 'Título',
      colStatus: 'Status',
      colAuthor: 'Autor',
      colPublished: 'Publicado',
      colUpdated: 'Atualizado',
      scheduledLabel: ({ date }: { date: string }) => `agendado · ${date}`,
    },
    postEditor: {
      backToPosts: '← Voltar para Posts',
      newPostTitle: 'Novo post',
      editPostTitleFallback: 'Editar post',

      titleLabel: 'Título',
      titlePlaceholder: 'Como o Embent responde perguntas de suporte',
      slugLabel: 'Slug',
      slugPlaceholder: 'how-embent-answers-support-questions',
      slugUrlPrefix: 'embent.com/blog/',
      excerptLabel: 'Resumo',
      excerptPlaceholder:
        'Uma ou duas frases — mostradas em /blog e usadas como meta descrição.',
      tagsLabel: 'Tags',
      tagsPlaceholder: 'rag, support, launch',
      tagsHint: 'Separadas por vírgula.',
      coverUrlLabel: 'URL da imagem de capa',
      coverUrlPlaceholder: 'https://cdn.embent.com/blog/…',

      authorLabel: 'Autor',
      editAuthorCta: 'Editar autor',
      newAuthorCta: '+ Novo autor',
      authorSelectPlaceholder: 'Escolha um autor',
      noAuthorsYet: 'Nenhum autor ainda — adicione um com "+ Novo autor" acima.',

      visibilityScheduledText: ({ date }: { date: string }) => `Vai ao ar ${date}`,
      visibilityLiveText: ({ date }: { date: string }) => `Publicado ${date}`,
      visibilityDraftText: 'Ainda não publicado',
      publishDateLabel: 'Data de publicação',
      publishDateHintPrefix: 'Os horários estão no fuso ',
      publishDateHintSuffix:
        " — o relógio deste navegador. Deixe em branco para publicar assim que você clicar em publicar. Defina um horário futuro e o post fica oculto até lá — ele some do blog, das contagens de tags, do sitemap e do feed RSS até o relógio chegar lá. Nada precisa estar rodando para ele aparecer.",

      bodyLabel: 'Corpo (Markdown)',
      livePreviewLabel: 'Prévia ao vivo',

      missingFieldLabel: {
        title: 'título',
        slug: 'slug',
        excerpt: 'resumo',
        body: 'corpo',
        author: 'autor',
      },
      beforeSavingAdd: ({ fields }: { fields: string }) => `Antes de salvar, adicione: ${fields}.`,

      savingCta: 'Salvando…',
      saveChangesCta: 'Salvar mudanças',
      saveDraftCta: 'Salvar rascunho',
      unpublishingCta: 'Despublicando…',
      schedulingCta: 'Agendando…',
      publishingCta: 'Publicando…',
      unpublishCta: 'Despublicar',
      scheduleCta: 'Agendar',
      publishCta: 'Publicar',
      savedFlash: 'Salvo',

      renderingPreview: 'Renderizando…',
      previewFailedPrefix: 'Falha ao carregar a prévia: ',

      uploadFailedFallback: 'Falha no envio',
      bodyPlaceholder: 'Escreva em Markdown… cole ou arraste uma imagem para enviá-la embutida no texto.',
      uploadingText: 'Enviando…',
      imageUploadFailedPrefix: 'Falha no envio da imagem: ',

      deleteCta: 'Excluir',
      deleteDialogTitle: ({ title }: { title: string }) => `Excluir "${title}"?`,
      deleteDialogDescriptionPrefix: 'Exclui este post permanentemente. Isso não pode ser desfeito. Digite',
      deleteDialogDescriptionSuffix: ' para confirmar.',
      confirmPostTitleAriaLabel: 'Confirmar título do post',
      cancelCta: 'Cancelar',
      deletingCta: 'Excluindo…',
      deletePostCta: 'Excluir post',

      editAuthorDialogTitle: ({ name }: { name: string }) => `Editar ${name}`,
      newAuthorDialogTitle: 'Novo autor',
      authorDialogDescription: 'Só autores reais — nenhuma identidade fabricada.',
      authorNameLabel: 'Nome',
      authorNamePlaceholder: 'Johnny Bui',
      authorRoleLabel: 'Função',
      authorRolePlaceholder: 'Fundador',
      authorAvatarLabel: 'URL do avatar',
      authorAvatarPlaceholder: 'https://cdn.embent.com/…',
      authorBioLabel: 'Bio',
      authorPersonaLabel: 'Persona (interno)',
      authorPersonaPlaceholder:
        'Escreve em frases curtas e diretas. Cético em relação a hype. Prefere um exemplo concreto a um adjetivo.',
      authorPersonaHint:
        "Uma nota de orientação para escrever com esta voz — nunca publicada, nunca mostrada aos leitores.",
      authorSavingCta: 'Salvando…',
      authorAddingCta: 'Adicionando…',
      saveAuthorCta: 'Salvar autor',
      addAuthorCta: 'Adicionar autor',

      writingGuideHeading: 'Guia de redação',
      writingGuideSubtitle: 'Suporte a Markdown, imagens e um prompt de briefing de IA',
      supportedMarkdownHeading: 'Markdown suportado',
      mdListHeadings: 'Títulos, níveis 1 a 6',
      mdListLists: 'Listas com marcadores e numeradas',
      mdListLinksPrefix: 'Links — ',
      mdListLinksSuffix: ', só http(s) e mailto',
      mdListImages:
        'Imagens em qualquer parte do corpo, inclusive no meio do artigo — cole ou arraste uma (veja abaixo)',
      mdListCode: 'Blocos de código em linha e destacados, com realce de sintaxe',
      mdListTables: 'Tabelas, no estilo GitHub, com alinhamento de coluna',
      mdListBlockquotes: 'Citações em bloco',
      strippedHeading: 'O que é removido',
      strippedPrefix: 'Todo o resto. Tags HTML brutas, e conteúdo de ',
      strippedSuffix:
        " especificamente, nunca sobrevivem — o corpo é higienizado contra uma lista fixa de permissões (títulos, listas, links, imagens, código, tabelas, citações, mais o parágrafo/ênfase de onde vêm) antes de renderizar em qualquer lugar, inclusive aqui na prévia.",
      imagesHeading: 'Imagens',
      imagesPrefix: 'Cole ou arraste uma imagem no corpo do texto — ela é enviada para cdn.embent.com e insere',
      imagesSuffix:
        ' no cursor, onde quer que ele esteja, para poder ficar no meio de um parágrafo. A imagem de capa acima é um campo separado — ela nunca vem de algo solto no corpo do texto.',
      copiedCta: 'Copiado!',
      copyAiPromptCta: 'Copiar prompt de IA',
      copiedToClipboard: 'Copiado para a área de transferência.',
      clipboardUnavailable:
        "A área de transferência não está disponível neste navegador — selecione e copie o prompt abaixo.",
      assemblesPrefix: 'Reúne o estilo da casa, ',
      assemblesAuthorPersonaClause: ({ authorName }: { authorName: string }) =>
        `a persona de ${authorName}`,
      assemblesNoAuthorClause: 'uma nota de que nenhum autor está selecionado',
      assemblesSuffix:
        ', as convenções de Markdown acima, e uma estrutura pedida — cole em um modelo externo, depois cole o rascunho de volta no corpo do texto.',

      aiPromptPersonaWithVoice: ({ authorName, persona }: { authorName: string; persona: string }) =>
        `Escreva com a voz de ${authorName}:\n${persona}`,
      aiPromptPersonaNoPersona: ({ authorName }: { authorName: string }) =>
        `${authorName} não tem persona definida — escreva em uma voz simples e direta.`,
      aiPromptAuthorFallbackLower: 'este autor',
      aiPromptAuthorFallbackUpper: 'Este autor',
      aiPromptTemplate: ({ personaSection }: { personaSection: string }) =>
        `Você está redigindo um post de blog para o Embent (embent.com), uma plataforma de agente de IA para sites.

Estilo da casa:
- Direto e concreto. Sem hype, sem discurso de marketing.
- Parágrafos curtos (2-4 frases).
- Nunca invente estatísticas ou nomes de clientes. Se você não tem um número real ou um cliente real, não invente um.

Voz do autor:
${personaSection}

Convenções de Markdown (isso é TUDO que vai renderizar — o resto é removido):
- Títulos (#, ##, ###), listas com marcadores e numeradas, citações em bloco.
- Links como [texto](url) — só http(s) e mailto.
- Imagens em qualquer parte do corpo, inclusive no meio do artigo, como ![alt](url).
- Blocos de código em linha e destacados.
- Tabelas (estilo GitHub).
- Tags HTML brutas e <script>/<style> são removidas por completo — escreva só Markdown puro, sem HTML.

Estrutura pedida:
1. Título
2. Um parágrafo de gancho
3. 4-7 seções, cada uma com um título h2 (##)
4. Um fechamento curto

Escreva o rascunho completo agora em Markdown.`,
    },
    feedback: {
      filterLabel: {
        unhandled: 'Não tratado',
        all: 'Todos',
      },
      typeLabel: {
        bug: 'Bug',
        feature: 'Recurso',
        question: 'Pergunta',
      },
      mailtoSubject: ({ id, type }: { id: string; type: string }) =>
        `Re: [Feedback do Embent ${id}] ${type}`,
      replyCta: 'Responder',
      colDate: 'Data',
      colWorkspace: 'Workspace',
      colUser: 'Usuário',
      colType: 'Tipo',
      colMessage: 'Mensagem',
      colActions: 'Ações',
      emptyUnhandled: 'Nenhum feedback pendente — caixa zerada.',
      emptyAll: 'Nenhum feedback ainda.',
      updateFeedbackErrorFallback: 'Não foi possível atualizar este feedback.',
      reopeningCta: 'Reabrindo…',
      markingCta: 'Marcando…',
      reopenCta: 'Reabrir',
      markHandledCta: 'Marcar como tratado',
      noBotFallback: 'nenhum bot',
      noUaFallback: 'nenhum UA',
    },
    playground: {
      demosAllTimeLabel: 'Demos no total',
      demos30dLabel: 'Demos · 30d',
      messagesAllTimeLabel: 'Mensagens no total',
      signedUpAllTimeLabel: 'Cadastrados',
      signedUp30dLabel: 'Cadastrados · 30d',
      leadsAllTimeLabel: 'Leads capturados',
      leads30dLabel: 'Leads · 30d',
      demosPerDayLabel: 'Demos por dia',
      messagesPerDayLabel: 'Mensagens por dia',
      demoUnit: ({ count }: { count: number }): string => (count === 1 ? 'demo' : 'demos'),
      messageUnit: ({ count }: { count: number }): string => (count === 1 ? 'mensagem' : 'mensagens'),
      noDemos30d: 'Nenhuma demo nos últimos 30 dias.',
      statusBreakdownHeading: 'Detalhamento por status (30d)',
      recentDomainsHeading: 'Domínios recentes',
      noDemosYet: 'Nenhuma demo ainda.',
      colHostname: 'Domínio',
      colStatus: 'Status',
      colPages: 'Páginas',
      colMessages: 'Mensagens',
      colAttempts: 'Tentativas',
      colSignedUp: 'Cadastrado',
      colLeads: 'Leads',
      colCreated: 'Criado',
      signedUpAriaLabel: 'cadastrado',
      notSignedUpAriaLabel: 'não cadastrado',
      noEmailFallback: '(sem e-mail)',
    },
  },
} satisfies Catalog;
